import {
    Button,
    Icon,
    Spinner,
} from '@nike/eds';
import {
 CaretDown, LocationPinFilled
} from '@nike/nike-design-system-icons';
import PropTypes from 'prop-types';
import React, {
 useCallback, useEffect, useMemo, useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNposFilters, setNposValue } from '../../../../redux/actions';
import { getBodyTranslations } from '../../../../redux/selectors';
import Auth from '../../../../utils/Auth';
import { filterRestrictedStores } from '../../../../utils/ej-utils';
import { sortStoresByNumber, unPadStoreNumber } from '../../../../utils/formatting';
import { getApplicationSource, isRetailLogin } from '../../../../utils/iframeMessaging';
import { storeViewsByCountry } from '../../../../utils/service-calls';
import nikeStoreCountries from '../../../../utils/static/countriesWithNikeStores';
import ListSelectorModal from '../reusable/ListSelectorModal';

const defaultStoreviewsFields = [
  'storeNumber',
  'name',
  'facilityType',
  'company',
  'address',
  'currencies',
  'id',
  'locale',
  'name',
  'phone',
  'timezone',
  'localizations',
  'storeConcept',
  'defaultLanguage'
];

const StoreFilter = ({ selectStore, selectCountry }) => {
    const [visible, setVisible] = useState(false);
    const [storesInCountry, setStoresInCountry] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    const dispatch = useDispatch();

    const currentStore = useSelector((state) => state.nposFilters.currentStore);
    const translations = useSelector(getBodyTranslations);
    const setCurrentStore = useCallback((store) => dispatch(setNposFilters({ currentStore: store })), [dispatch]);

    useEffect(() => {
      const abortController = new AbortController();

      const getStoresByCountry = async (country) => {
        if (!country?.value || abortController.signal.aborted) {
          return;
        }

        try {
          // For NPOS fetch stores that are published
          const res = await storeViewsByCountry(country.value, true, [...new Set([...defaultStoreviewsFields])]);

          // Check if component was unmounted during the async operation
          if (abortController.signal.aborted) {
            return;
          }

          if (!res.body || !res.body.objects || !Array.isArray(res.body.objects)) {
            // eslint-disable-next-line fp/no-throw
            throw new Error(res.statusText);
          }
          const stores = res.body.objects.map((storeInCountry) => {
            const label = `${unPadStoreNumber(storeInCountry.storeNumber)} - ${storeInCountry.name}`;
            return ({ full: storeInCountry, label, value: storeInCountry.id });
          });

          if (isRetailLogin() && !abortController.signal.aborted) {
            const storeId = Auth.getStoreId();
            const currentStores = stores.find((store) => store.value === storeId);
            setCurrentStore(currentStores);
            selectStore(currentStores);
            setStoresInCountry(stores);
          }
        } catch (err) {
          if (!abortController.signal.aborted) {
            setErrorMessage(err.message);
            setStoresInCountry([]);
          }
        }
      };

      async function fetchData() {
        const retailLogin = isRetailLogin();
        if (retailLogin && !abortController.signal.aborted) {
          const country = await Auth.getCountriesForRetailLogin(nikeStoreCountries);
          if (country.length === 1 && !abortController.signal.aborted) {
            selectCountry(country[0]);
            getStoresByCountry(country[0]);
          }
        }
      }
      fetchData();

      // Cleanup function to abort any ongoing operations
      return () => {
        abortController.abort();
      };
    }, [selectCountry, selectStore, setCurrentStore, dispatch]);

    const onStoreChange = (store) => {
      if (currentStore.value !== store.value) {
        dispatch(setNposValue({ isStoreSwitched: true }));
      }
      selectStore(store);
      setCurrentStore(store);
    };

    const stores = useMemo(() => {
      const appSource = getApplicationSource();
      const sortedStores = sortStoresByNumber(storesInCountry.map((store) => ({ ...store, id: store.value })));
      const filteredStores = filterRestrictedStores(sortedStores, currentStore, appSource);
      return filteredStores;
    }, [storesInCountry, currentStore]);

    return (
      <div className="mt-sm-10" style={{ zIndex: visible ? 1000 : 0 }}>
        <div className="d-sm-flx">
          <Button
            afterSlot={<CaretDown className="eds-color--secondary" />}
            beforeSlot={<LocationPinFilled />}
            data-test-id="store-select-button"
            variant="ghost"
            onClick={() => (currentStore?.label ? setVisible(!visible) : null)}
          >
            {currentStore?.label || <Spinner />}
          </Button>
          {errorMessage && <div className="pl4-sm error">Error : {errorMessage}</div>}
        </div>
        <ListSelectorModal
          doneButtonProps={{ enabled: true, label: translations.RL_Done, onClick: onStoreChange }}
          doneButtonRequired={false}
          id="store-selection-modal"
          listHeight="50vh"
          listItemProps={{ listIcon: <Icon name="LocationPinFilled" size="m" />, selectionIcon: <Icon name="Check" size="m" /> }}
          options={stores}
          searchFieldProps={{
                    enabled: true,
                    noSearchResultsMessage: 'No stores found',
                    placeholder: translations.RL_StoreSearch
                }}
          selectedOption={{ ...currentStore, id: currentStore.value }}
          setVisible={setVisible}
          title={translations.RL_StoreSelection}
          visible={visible}
        />
      </div>
    );
};

StoreFilter.propTypes = {
  selectCountry: PropTypes.func.isRequired,
  selectStore: PropTypes.func.isRequired,
};

export default StoreFilter;
